/**
 * Finanzas personales — tipos + helpers de formato/fecha.
 * fmt() portado de panel-anaafernandez/src/data.js (ARS: 1000000 -> $1.000.000).
 */
import { differenceInCalendarDays, parseISO } from "date-fns";

/** Secciones del plan + diario vía API externa (api/lib/finanzas/schema.ts). */
export type FinanzasSection =
  | "reconcile" | "not_in_list" | "hoy_pagado" | "julio" | "agosto"
  | "deuda" | "ingreso" | "suplementario"
  | "gasto" | "transferencia" | "ahorro";
/** Categorías plan + bot externo (mismas enums que /api/finanzas/v1). */
export type FinanzasCategory =
  | "credito" | "tarjeta" | "fijo" | "servicios"
  | "comida" | "transporte" | "hogar" | "salud" | "ocio"
  | "suscripciones" | "salario" | "otro";
export type FinanzasStatus = "pending" | "paid" | "mora" | "cancelled";
export type FinanzasCurrency = "ARS" | "USD";

/**
 * Secciones del diario vía API externa → se proyectan al libro Transacciones.
 * La integración externa no se nombra en UI (solo “API” / “Diario”).
 */
export const LEDGER_ITEM_SECTIONS: readonly FinanzasSection[] = [
  "gasto", "ingreso", "transferencia", "ahorro",
] as const;

export interface FinanzasItem {
  id: number;
  section: FinanzasSection;
  label: string;
  category: FinanzasCategory | null;
  amount: number | null;
  amount_planned: number | null;
  due_date: string | null; // ISO yyyy-mm-dd
  due_label: string | null;
  status: FinanzasStatus | null;
  icon: string | null;
  note: string | null;
  sort_order: number;
  /** Moneda del movimiento (API v1). Default ARS. */
  currency?: FinanzasCurrency | string | null;
  /** p.ej. { source: "whatsapp", wa_message_id } desde la API externa. */
  metadata?: Record<string, unknown> | null;
  /** ISO timestamps from DB — fallback when due_date is null (month filter). */
  created_at?: string | null;
  updated_at?: string | null;
}
export interface FinanzasLoanRow {
  id: number;
  cuota_n: number;
  period_label: string | null;
  period_date: string | null;
  capital: number | null;
  interest: number | null;
  balance: number | null;
  sort_order: number;
}
export interface FinanzasHistoryRow {
  id: number;
  month_label: string | null;
  month_date: string | null;
  naranjax: number | null;
  uala: number | null;
  sort_order: number;
}
export interface FinanzasMeta {
  id: number;
  person_name: string | null;
  gestor: string | null;
  alert_text: string | null;
  alert_subtext: string | null;
  loan_capital: number | null;
  loan_neto: number | null;
  loan_total: number | null;
  loan_tna: string | null;
  loan_active: boolean;
  loan_first_date: string | null;
  /** Override manual de la cotización Dólar Oficial (ARS por 1 USD). Fallback si dolarapi falla. */
  usd_ars_rate: number | null;
}

// ── Oruon: transacciones + recurrentes ───────────────────────────────────────────
export type TxKind = "income" | "expense";
export type TxOrigin = "manual" | "recurring" | "import";
export type RecurringFrequency = "weekly" | "monthly" | "yearly";

export interface FinanzasTransaction {
  id: number;
  tx_date: string; // ISO yyyy-mm-dd
  description: string;
  kind: TxKind;
  amount: number; // magnitud positiva; el signo lo da `kind`
  currency: FinanzasCurrency;
  category: string | null;
  account: string | null;
  origin: TxOrigin;
  recurring_id: number | null;
  note: string | null;
  sort_order: number;
}

export interface FinanzasRecurring {
  id: number;
  name: string;
  kind: TxKind;
  amount: number;
  currency: FinanzasCurrency;
  category: string | null;
  frequency: RecurringFrequency;
  day_of_month: number | null;
  next_date: string | null; // ISO yyyy-mm-dd
  icon: string | null;
  active: boolean;
  note: string | null;
  sort_order: number;
  /**
   * Human review of seed/hardcoded rows:
   * - null  → pending (UI orange + Sí/No: ¿es recurrente o gasto único?)
   * - true  → confirmed recurring
   * - false → demoted to one-off gasto (should not stay in active recurrings)
   */
  user_validated?: boolean | null;
}

/** Pending human review (seed / hardcode). */
export function isRecurringPendingValidation(r: Pick<FinanzasRecurring, "user_validated">): boolean {
  return r.user_validated == null;
}

/** Ingreso REAL (read-only) MercadoPago. Finanzas personales NO incluye Stripe. */
export interface FinanzasRealTx {
  id: string; // "mp:<txn>"
  tx_date: string; // ISO yyyy-mm-dd
  description: string;
  source: "mercadopago";
  kind: "income";
  is_subscription: boolean;
  currency: FinanzasCurrency;
  gross: number; // unidades mayores
  net: number | null;
  customer_email: string | null;
  customer_name?: string | null;
  transaction_number?: string | null;
  transaction_type?: string | null;
  payment_method_id?: string | null;
  payment_type_id?: string | null;
  operation_type?: string | null;
  external_reference?: string | null;
  cashflow?: "in" | "out" | "neutral" | null;
  summary_es?: string | null;
  is_loan?: boolean;
  loan_kind?: string | null;
  merchant_key?: string | null;
  poi_sub_type?: string | null;
  poi_branch?: string | null;
  wallet_balance_before?: number | null;
  wallet_balance_after?: number | null;
  funding_trace?: WalletOutflowTrace | null;
  money_in?: MoneyInDetail | null;
}
/** Run-rate mensual de suscripciones ACTIVAS MP (lo comprometido, no lo cobrado). */
export interface FinanzasMrr {
  activeCount: number;
  usd: number; // legacy; personal finance is MP-only (usually 0)
  ars: number; // MRR mensual en ARS (subs activas MP)
  stripeCount: number; // always 0 in personal finance
  mpCount: number;
}

export interface MoneyInDetail {
  kind: "sale" | "subscription" | "loan" | "transfer_in" | "account_fund" | "other_in";
  kind_label: string;
  counterparty_role:
    | "buyer"
    | "payer_mp"
    | "account_self"
    | "mercadopago_credit"
    | "origin_unknown"
    | "unknown";
  counterparty_email: string | null;
  counterparty_name: string | null;
  email_is_not_origin?: boolean;
  gross: number;
  net: number | null;
  fee: number | null;
  currency: "ARS" | "USD";
  pack_id: string | null;
  installments: number | null;
  total_paid: number | null;
  mp_status: string | null;
  mp_status_detail: string | null;
  operation_type: string | null;
  payment_method_id: string | null;
  payment_type_id: string | null;
  business_unit: string | null;
  business_sub_unit: string | null;
  poi_type: string | null;
  poi_sub_type: string | null;
  poi_branch: string | null;
  external_reference: string | null;
  summary_es: string | null;
  narrative_es: string;
}

export interface WalletFundingInflow {
  id: string;
  tx_date: string;
  description: string;
  amount: number;
  kind: "sale" | "transfer_in" | "loan" | "other_in";
  email?: string | null;
  net?: number | null;
  money_in?: MoneyInDetail | null;
}

export interface WalletMoneySourceRollup {
  key: string;
  label: string;
  kind: "sale" | "transfer_in" | "loan" | "other_in" | "mixed";
  sum: number;
  n: number;
  email: string | null;
}

export interface WalletOutflowTrace {
  id: string;
  tx_date: string;
  month: string;
  description: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  covered_by_balance: boolean;
  shortfall: number;
  money_in_before?: number;
  n_in_before?: number;
  money_out_before?: number;
  n_out_before?: number;
  prior_inflows_month: WalletFundingInflow[];
  prior_inflows_month_sum: number;
  prior_inflows_window?: WalletFundingInflow[];
  prior_inflows_window_sum?: number;
  sources?: WalletMoneySourceRollup[];
  funding_chain: WalletFundingInflow[];
  funding_chain_sum: number;
  narrative_es?: string;
  activity: string | null;
  operation_type: string | null;
}

export interface WalletMonthRollup {
  month: string;
  label: string;
  opening: number;
  in_total: number;
  out_total: number;
  net: number;
  closing: number;
  n_in: number;
  n_out: number;
  transfer_out: number;
  n_transfer_out: number;
  solvent: boolean;
}

/** Movimiento de CUENTA MP (extracto real): transferencias, ingresos, compras. */
export interface FinanzasAccountTx {
  id: string; // "mpacc:<txn>"
  tx_date: string;
  description: string;
  source: "mercadopago";
  direction: "in" | "out";
  activity: "transferencia" | "ingreso_dinero" | "compra" | "suscripcion_servicio" | "prestamo" | "otro";
  currency: FinanzasCurrency;
  amount: number;
  status: "completed" | "pending" | "refunded";
  operation_type: string | null;
  customer_email?: string | null;
  customer_name?: string | null;
  transaction_number?: string | null;
  transaction_type?: string | null;
  payment_method_id?: string | null;
  payment_type_id?: string | null;
  business_sub_unit?: string | null;
  poi_type?: string | null;
  cashflow?: "in" | "out" | "neutral" | null;
  external_reference?: string | null;
  summary_es?: string | null;
  net_amount?: number | null;
  is_loan?: boolean;
  loan_kind?: string | null;
  merchant_key?: string | null;
  poi_sub_type?: string | null;
  poi_branch?: string | null;
  wallet_balance_before?: number | null;
  wallet_balance_after?: number | null;
  funding_trace?: WalletOutflowTrace | null;
  money_in?: MoneyInDetail | null;
  email_role?: "payer_mp" | "buyer" | "unknown" | null;
  fee_amount?: number | null;
  pack_id?: string | null;
  mp_status?: string | null;
  mp_status_detail?: string | null;
  business_unit?: string | null;
  installments?: number | null;
}

export interface FinanzasReal {
  incomeTransactions: FinanzasRealTx[];
  /** Extracto de cuenta MP — complemento del income feed, sin solapamiento. */
  accountActivity?: FinanzasAccountTx[];
  generatedAt: string;
  brand: string;
  mrr: FinanzasMrr | null;
  expenses: null; // fase: gastos/depósitos de cuenta MP
  /** Always postgres for Transacciones performance contract. */
  sourceOfTruth?: "postgres";
  /** Always false on panel path — MP HTTP only in webhooks. */
  hitExternalApis?: boolean;
  tables?: readonly string[];
  /** Filtro MP: transferencias y wallet spend excluidos del feed de ingresos. */
  mpFilter?: {
    lookbackDays: number;
    businessIncomeCount: number;
    note: string;
  };
  /** Saldo wallet ARS reconstruido en la ventana (base 0) + mes a mes + capacidad. */
  walletBalance?: {
    currency: "ARS";
    opening: number;
    closing: number;
    lookbackDays: number;
    note: string;
    byMonth?: WalletMonthRollup[];
    largeOutflows?: WalletOutflowTrace[];
  } | null;
}

export interface FinanzasData {
  meta: FinanzasMeta | null;
  items: FinanzasItem[];
  loan: FinanzasLoanRow[];
  history: FinanzasHistoryRow[];
  transactions: FinanzasTransaction[];
  recurring: FinanzasRecurring[];
  /** Feed read-only de ingresos reales MercadoPago (sin Stripe). null si no disponible. */
  real?: FinanzasReal | null;
}

/** Colores por categoría (plan + bot API). */
export const TCOLOR: Record<FinanzasCategory, string> = {
  credito: "#ef4444",
  tarjeta: "#f59e0b",
  fijo: "#3b82f6",
  servicios: "#10b981",
  comida: "#f97316",
  transporte: "#06b6d4",
  hogar: "#a78bfa",
  salud: "#34d399",
  ocio: "#f472b6",
  suscripciones: "#818cf8",
  salario: "#4ade80",
  otro: "#94a3b8",
};

export const SECTION_LABELS: Record<FinanzasSection, string> = {
  reconcile: "Reconciliación (lista vs. real)",
  not_in_list: "Estaban en las fechas pero NO en la lista",
  hoy_pagado: "Pagado el día de cierre",
  julio: "Julio — pendientes",
  agosto: "Agosto — lo que se viene",
  deuda: "Deuda vigente",
  ingreso: "Ingresos (previstos)",
  suplementario: "Suplementario",
  gasto: "Gastos diarios (API)",
  transferencia: "Transferencias",
  ahorro: "Ahorro",
};

/**
 * Fecha de libro para un item del diario/API.
 * - due_date if set
 * - else created_at only (never updated_at — bulk plan saves bump updated_at and
 *   would wrongly move undated rows into “this month”)
 * Never invents “today”.
 */
export function itemLedgerDate(it: Pick<FinanzasItem, "due_date" | "updated_at" | "created_at">): string | null {
  if (it.due_date && /^\d{4}-\d{2}-\d{2}/.test(it.due_date)) return it.due_date.slice(0, 10);
  const raw = it.created_at;
  if (!raw) return null;
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return null;
  // UTC calendar day — server/DB timestamps are timestamptz; avoid host-local shift.
  const d = new Date(t);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export const ALL_CATEGORIES: FinanzasCategory[] = [
  "credito", "tarjeta", "fijo", "servicios",
  "comida", "transporte", "hogar", "salud", "ocio",
  "suscripciones", "salario", "otro",
];

/** Formato ARS. d=2 muestra decimales. */
export function fmt(n: number | null | undefined, d = 0): string {
  if (n == null || Number.isNaN(n)) return "—";
  const s = Math.abs(n).toFixed(d);
  const [i, f] = s.split(".");
  const ii = i.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const sign = n < 0 ? "-" : "";
  return sign + "$" + (d > 0 ? `${ii},${f}` : ii);
}

/**
 * Días desde hoy hasta una fecha ISO yyyy-mm-dd (negativo = vencido).
 * Parsea a medianoche LOCAL (no UTC de parseISO) para no corrarse un día en AR.
 */
export function daysUntil(iso: string | null, today: Date = new Date()): number | null {
  if (!iso) return null;
  try {
    const raw = iso.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return differenceInCalendarDays(parseISO(iso), today);
    const target = new Date(`${raw}T00:00:00`);
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return differenceInCalendarDays(target, start);
  } catch {
    return null;
  }
}

/** Labels de sección del plan; `julio`/`agosto` se muestran como mes actual/próximo. */
export function sectionLabelForToday(section: FinanzasSection, today: Date = new Date()): string {
  const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const monthName = (offset: number) => {
    const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    return cap(new Intl.DateTimeFormat("es-AR", { month: "long" }).format(d));
  };
  if (section === "julio") return `${monthName(0)} — pendientes`;
  if (section === "agosto") return `${monthName(1)} — lo que se viene`;
  return SECTION_LABELS[section];
}

/** "jueves 26 de junio de 2026" */
export function formatTodayEsAR(today: Date = new Date()): string {
  return new Intl.DateTimeFormat("es-AR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  }).format(today);
}

/** Suma de montos de items activos (excluye paid/cancelled). */
export function sumActive(items: FinanzasItem[]): number {
  return items.reduce(
    (s, x) => (x.status === "paid" || x.status === "cancelled" ? s : s + (x.amount ?? 0)),
    0,
  );
}

// ── Formato de moneda (Oruon) ────────────────────────────────────────────────────
// El panel viejo usa fmt() con "$". Oruon muestra "5.560.000,00 ARS" / "200 USD"
// (número agrupado + código, sin "$"), así que la capa nueva usa fmtNum/fmtMoney.

/** Número agrupado sin símbolo. 1000000 -> "1.000.000"; d=2 -> "1.000.000,00". */
export function fmtNum(n: number | null | undefined, d = 0): string {
  if (n == null || Number.isNaN(n)) return "—";
  const s = Math.abs(n).toFixed(d);
  const [i, f] = s.split(".");
  const ii = i.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const sign = n < 0 ? "-" : "";
  return sign + (d > 0 ? `${ii},${f}` : ii);
}

/** "5.560.000 ARS" / "200 USD". */
export function fmtMoney(n: number | null | undefined, currency: FinanzasCurrency, d = 0): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${fmtNum(n, d)} ${currency}`;
}

/**
 * Conteos del extracto MP (Transacciones).
 * Cobros = ingresos de marca con realTx y sin is_loan (préstamos van aparte).
 * Debe coincidir con el filtro "cobros" de la UI.
 */
export function countMpExtractRows(
  rows: ReadonlyArray<{
    is_loan?: boolean;
    accountTx?: unknown;
    realTx?: unknown;
    real?: boolean;
    kind?: string;
    category?: string | null;
  }>,
): { all: number; cobros: number; cuenta: number; prestamo: number; fees: number } {
  const c = { all: rows.length, cobros: 0, cuenta: 0, prestamo: 0, fees: 0 };
  for (const r of rows) {
    if (r.is_loan) c.prestamo++;
    if (r.accountTx) c.cuenta++;
    else if (r.real && r.kind === "expense" && r.category === "Comisiones") c.fees++;
    else if (r.realTx && !r.is_loan) c.cobros++;
  }
  return c;
}

/** Compacto para chips del calendario: 5560000 -> "$5,6M", 4000 -> "$4k". */
export function fmtCompact(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${fmtNum(abs / 1_000_000, abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`;
  return `${sign}$${fmtNum(abs)}`;
}

/** Convierte `amount` de una moneda a otra con la cotización (ARS por 1 USD). null si falta cotización. */
export function inCurrency(
  amount: number | null | undefined,
  from: FinanzasCurrency,
  to: FinanzasCurrency,
  rate: number | null,
): number | null {
  if (amount == null || Number.isNaN(amount)) return null;
  if (from === to) return amount;
  if (!rate || rate <= 0) return null;
  return from === "USD" ? amount * rate : amount / rate;
}

/** Monto con signo (ingreso +, gasto −) ya convertido a la moneda de display. */
export function signedInDisplay(
  tx: Pick<FinanzasTransaction, "amount" | "currency" | "kind">,
  display: FinanzasCurrency,
  rate: number | null,
): number | null {
  const v = inCurrency(tx.amount, tx.currency, display, rate);
  if (v == null) return null;
  return tx.kind === "income" ? v : -v;
}

// ── Recurrencia (ocurrencias por mes / próxima) ──────────────────────────────────
function isoOf(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function parseLocalISO(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

/** Días del mes (año, mes 0-index) en los que cae la recurrente. */
export function occurrencesInMonth(
  rec: Pick<FinanzasRecurring, "frequency" | "day_of_month" | "next_date">,
  year: number,
  month0: number,
): number[] {
  const dim = new Date(year, month0 + 1, 0).getDate();
  const clamp = (d: number) => Math.min(Math.max(1, Math.round(d)), dim);
  if (rec.frequency === "monthly") {
    const d = rec.day_of_month ?? (rec.next_date ? parseLocalISO(rec.next_date).getDate() : null);
    return d ? [clamp(d)] : [];
  }
  if (rec.frequency === "yearly") {
    if (!rec.next_date) return [];
    const nd = parseLocalISO(rec.next_date);
    return nd.getMonth() === month0 ? [clamp(nd.getDate())] : [];
  }
  // weekly: mismo día de semana que next_date (o day_of_month interpretado como weekday 0-6)
  let wd: number | null = null;
  if (rec.next_date) wd = parseLocalISO(rec.next_date).getDay();
  else if (rec.day_of_month != null) wd = ((rec.day_of_month % 7) + 7) % 7;
  if (wd == null) return [];
  const days: number[] = [];
  for (let d = 1; d <= dim; d++) if (new Date(year, month0, d).getDay() === wd) days.push(d);
  return days;
}

/** ISO de la próxima ocurrencia >= `from` (busca hasta 13 meses adelante). */
export function nextOccurrence(rec: FinanzasRecurring, from: Date = new Date()): string | null {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  for (let i = 0; i < 13; i++) {
    const base = from.getMonth() + i;
    const yy = from.getFullYear() + Math.floor(base / 12);
    const mm = ((base % 12) + 12) % 12;
    for (const d of occurrencesInMonth(rec, yy, mm)) {
      const cand = new Date(yy, mm, d);
      if (cand >= start) return isoOf(cand);
    }
  }
  return rec.next_date ?? null;
}

/** Equivalente mensual del monto (para totales): weekly*52/12, yearly/12. En su propia moneda. */
export function monthlyEquivalent(rec: Pick<FinanzasRecurring, "amount" | "frequency">): number {
  const a = rec.amount ?? 0;
  if (rec.frequency === "weekly") return (a * 52) / 12;
  if (rec.frequency === "yearly") return a / 12;
  return a;
}

// ── Metadatos de categorías (Oruon) ──────────────────────────────────────────────
export const FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
  weekly: "Semanal",
  monthly: "Mensual",
  yearly: "Anual",
};

/**
 * Categorías de transacción sugeridas (datalist) con emoji, basadas en los gastos
 * REALES de Ana (alquiler, expensas, monotributo, obra social, tarjetas/créditos
 * Naranja X · Ualá · Mercado Pago). Los emojis por marca van en las recurrentes.
 */
export const TX_CATEGORY_META: { key: string; label: string; emoji: string }[] = [
  { key: "Ingresos", label: "Ingresos", emoji: "💵" },
  { key: "Alquiler", label: "Alquiler", emoji: "🏠" },
  { key: "Expensas", label: "Expensas", emoji: "🏢" },
  { key: "Tarjetas", label: "Tarjetas", emoji: "💳" },
  { key: "Créditos", label: "Créditos", emoji: "🏦" },
  { key: "Impuestos", label: "Impuestos", emoji: "🧾" },
  { key: "Salud", label: "Salud", emoji: "🏥" },
  { key: "Servicios", label: "Servicios", emoji: "📱" },
  { key: "Comida", label: "Comida", emoji: "🛒" },
  { key: "Transporte", label: "Transporte", emoji: "🚗" },
  { key: "Comisiones", label: "Comisiones", emoji: "🔵" },
  { key: "Otros", label: "Otros", emoji: "📦" },
];
export function txCategoryEmoji(cat: string | null): string {
  if (!cat) return "🏷️";
  return TX_CATEGORY_META.find((c) => c.key.toLowerCase() === cat.toLowerCase())?.emoji ?? "🏷️";
}

/**
 * Grupos de recurrentes (chips del filtro), basados en los gastos REALES de Ana.
 * `todas` es el "ver todo".
 */
export const RECURRING_CATEGORIES: { key: string; label: string; emoji: string }[] = [
  { key: "todas", label: "Todas", emoji: "✨" },
  { key: "ingresos", label: "Ingresos", emoji: "💵" },
  { key: "vivienda", label: "Vivienda", emoji: "🏠" },
  { key: "tarjetas", label: "Tarjetas", emoji: "💳" },
  { key: "creditos", label: "Créditos", emoji: "🏦" },
  { key: "impuestos", label: "Impuestos", emoji: "🧾" },
  { key: "salud", label: "Salud", emoji: "🏥" },
  { key: "servicios", label: "Servicios", emoji: "📱" },
  { key: "otros", label: "Otros", emoji: "📦" },
];
export function recurringCategoryMeta(key: string | null) {
  return RECURRING_CATEGORIES.find((c) => c.key === (key ?? "")) ?? { key: key ?? "otros", label: key ?? "Otros", emoji: "📦" };
}

/**
 * Gastos recurrentes REALES de Ana (presets de alta rápida en Recurrentes).
 * Emojis por marca: Naranja X 🟠 (naranja), Ualá 💜 (violeta), Mercado Pago 💙
 * (celeste). Montos de referencia (editables al agregar).
 */
export const GASTOS_FRECUENTES_CATALOG: {
  name: string; icon: string; amount: number; currency: FinanzasCurrency; category: string;
}[] = [
  { name: "Alquiler", icon: "🏠", amount: 350000, currency: "ARS", category: "vivienda" },
  { name: "Expensas", icon: "🏢", amount: 100000, currency: "ARS", category: "vivienda" },
  { name: "Naranja X", icon: "🟠", amount: 194000, currency: "ARS", category: "tarjetas" },
  { name: "Ualá", icon: "💜", amount: 135000, currency: "ARS", category: "creditos" },
  { name: "Mercado Pago", icon: "💙", amount: 22000, currency: "ARS", category: "creditos" },
  { name: "Monotributo", icon: "🧾", amount: 48250, currency: "ARS", category: "impuestos" },
  { name: "Obra Social", icon: "🏥", amount: 40000, currency: "ARS", category: "salud" },
  { name: "Teléfono", icon: "📱", amount: 25000, currency: "ARS", category: "servicios" },
];
